/**
 * Connectors list page component.
 * Marketplace/browse screen for connectors (Zapier-style).
 */

// React Imports
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Grid,
  Card,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';
import { ConnectorCard } from '../components/connectors/ConnectorCard';
import { ConnectorIcon } from '../components/connectors/ConnectorIcon';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';
import type { ConnectorDto, ConnectorFilters } from '../types/connector';

/**
 * Connectors list page component.
 */
export const ConnectorsListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [connectors, setConnectors] = useState<ConnectorDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const filters: ConnectorFilters = {
    category: selectedCategory || undefined,
    isActive: true,
  };

  const loadConnectors = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await connectorService.getConnectors(filters);
      setConnectors(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connectors';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    connectors.forEach((c) => {
      if (c.category) cats.add(c.category);
    });
    return Array.from(cats).sort();
  }, [connectors]);

  const filteredConnectors = useMemo(() => {
    if (!searchTerm) return connectors;
    const searchLower = searchTerm.toLowerCase();
    return connectors.filter(
      (c) =>
        c.name.toLowerCase().includes(searchLower) ||
        c.description?.toLowerCase().includes(searchLower) ||
        c.vendor?.toLowerCase().includes(searchLower) ||
        c.category?.toLowerCase().includes(searchLower)
    );
  }, [connectors, searchTerm]);

  const handleConnect = (connector: ConnectorDto) => {
    navigate(`/connectors/${connector.id}/instances/new`);
  };

  const handleView = (connector: ConnectorDto) => {
    navigate(`/connectors/${connector.id}`);
  };

  // Table columns for list view
  const tableColumns: DataTableColumn<ConnectorDto>[] = [
    {
      id: 'name',
      label: 'Connector',
      minWidth: 250,
      render: (connector) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ConnectorIcon
            connectorKey={connector.key}
            category={connector.category}
            sx={{ fontSize: 32 }}
          />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {connector.name}
            </Typography>
            {connector.vendor && (
              <Typography variant="caption" color="text.secondary">
                {connector.vendor}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      minWidth: 150,
      render: (connector) =>
        connector.category ? (
          <Chip label={connector.category} size="small" variant="outlined" />
        ) : null,
    },
    {
      id: 'capabilities',
      label: 'Capabilities',
      minWidth: 120,
      render: (connector) => (
        <Typography variant="body2">{connector.capabilityCount}</Typography>
      ),
    },
    {
      id: 'instances',
      label: 'Instances',
      minWidth: 100,
      render: (connector) => (
        <Typography variant="body2">{connector.instanceCount}</Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'right',
      render: (connector) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleView(connector)}
          >
            View
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleConnect(connector)}
          >
            Connect
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Connectors"
      createButton={{
        label: 'Browse All',
        onClick: () => navigate('/connectors'),
        icon: <AddIcon />,
      }}
      viewMode="both"
      activeView={viewMode === 'grid' ? 'grid' : 'table'}
      onViewChange={(view) => setViewMode(view === 'grid' ? 'grid' : 'list')}
      filters={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            placeholder="Search connectors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />
          {/* Category Filters */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              onClick={() => setSelectedCategory('')}
              color={selectedCategory === '' ? 'primary' : 'default'}
              variant={selectedCategory === '' ? 'filled' : 'outlined'}
            />
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={!loading && filteredConnectors.length === 0}
      emptyState={{
        title: 'No connectors found',
        description: searchTerm
          ? `No connectors match "${searchTerm}". Try a different search term.`
          : 'No connectors are available at this time.',
        action: searchTerm ? { label: 'Clear Search', onClick: () => setSearchTerm('') } : undefined,
      }}
    >
      {viewMode === 'grid' ? (
        <Grid spacing={3}>
          {filteredConnectors.map((connector) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={connector.id}>
              <ConnectorCard
                connector={connector}
                viewMode="grid"
                onConnect={handleConnect}
                onView={handleView}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <DataTable
            columns={tableColumns}
            rows={filteredConnectors}
          />
        </Card>
      )}
    </PageListLayout>
  );
};
